import { useSearchParams, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiPlus, FiTrash2, FiChevronDown, FiChevronUp, FiPrinter, FiEdit2 } from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import ShareDialog from '../components/ShareDialog';
import { fetchEvents } from '../apiService';
import { generateStrategyRecommendations } from './strategyRules';
import './PlayBook.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

function PlayBook() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Arriving from the sidebar's per-show links, so open that show directly
  // instead of showing the picker.
  const [searchParams] = useSearchParams();
  const eventIdFromUrl = searchParams.get('eventId');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [playbookId, setPlaybookId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState('');
  const [generatingRecs, setGeneratingRecs] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [editingRecs, setEditingRecs] = useState(false);
  

  // Section expand states
  const [sections, setSections] = useState({
    showOverview: false,
    generalInfo: false,
    travelInfo: false,
    teamSchedule: false,
    marketingSchedule: false,
    boothMaterials: false,
    projectOverview: false,
  });

  // Show Overview form
  const [showOverview, setShowOverview] = useState({
    showName: '', exhibitHall: '', eventDates: '', targetAudience: '',
    location: '', locationAddress: '', notes: '',
  });

  // General Info form
  const [generalInfo, setGeneralInfo] = useState({
    boothNumber: '', boothSize: '',
    showAttireNotes: '', badgesAndBoothConduct: '', badgePickup: '',
  });

  // Travel entries
  const [travelEntries, setTravelEntries] = useState([{
    representativeName: '', arrivalDate: '', arrivalTime: '', departureDate: '', departureTime: '',
    hotelReservationNumber: '', hotelTotalCost: '', hotelName: '', hotelAddress: '',
    airfareCost: '', carMileageCost: '', trainCost: '', rentalCarCost: '',
  }]);

  // Team members
  const [teamMembers, setTeamMembers] = useState([
    { memberName: '', contactTitle: '', startDate: '', startTime: '', endDate: '', endTime: '' },
    { memberName: '', contactTitle: '', startDate: '', startTime: '', endDate: '', endTime: '' },
    { memberName: '', contactTitle: '', startDate: '', startTime: '', endDate: '', endTime: '' },
  ]);
  const [teamDates, setTeamDates] = useState({ arrivalDate: '', departureDate: '' });
  const [teamNotes, setTeamNotes] = useState('');

  // Marketing entries
  const [marketingEntries, setMarketingEntries] = useState([
    { mediumName: '', scheduleDate: '', keyMessages: '', callToAction: '', representativeName: '' },
    { mediumName: '', scheduleDate: '', keyMessages: '', callToAction: '', representativeName: '' },
    { mediumName: '', scheduleDate: '', keyMessages: '', callToAction: '', representativeName: '' },
  ]);
  const [marketingRep, setMarketingRep] = useState('');

  // Booth materials
  const [boothForm, setBoothForm] = useState({
    itemType: '', storageLocation: '', storageArea: '', quantity: '',
    eventLocation: '', startDate: '', endDate: '', status: '',
    description: '', itemWeight: '0', itemLength: '0', itemWidth: '0', itemHeight: '0',
    pkgWeight: '0', pkgLength: '0', pkgWidth: '0', pkgHeight: '0',
  });
  const [boothItems, setBoothItems] = useState([]);

  // Strategy Builder
  const [strategyStep, setStrategyStep] = useState(0);
  const [strategyAnswers, setStrategyAnswers] = useState({});

  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewEditing, setPreviewEditing] = useState(false);
  const [strategyText, setStrategyText] = useState('');

  // Progress
  const [progress, setProgress] = useState(0);

  const strategyQuestions = {
    'Lead Generation': [
      { q: 'Have you exhibited at this show before?', options: ['Never', '1-2 times', 'Repeat exhibitor', 'Long-time exhibitor'] },
      { q: 'How well known is your brand in this market?', options: ['Unknown', 'Some awareness', 'Industry leader'] },
      { q: 'Is the attendee list available with email before the show?', options: ['Yes', 'No'] },
      { q: 'Do you have your own list of contacts attending the show?', options: ['Yes', 'No'], hasFollowUp: true, followUpLabel: 'Please share how to reach them:', followUpPlaceholder: 'Phone / Email / Address' },
      { q: 'Is anyone from your company speaking at the event?', options: ['Yes', 'No'] },
      { q: 'Which giveaway approach best applies to your company?', options: ['We can give away our own product or service', 'We can offer a free trial or discount', 'A product/service outside our offerings'], multi: true },
      { q: 'What is your budget for this show?', options: ['Small', 'Medium', 'Large'] },
      { q: 'What is your booth size?', options: ['10x10', '10x20', 'Island', 'Large experience'] },
    ],
    'Brand Awareness': [
      { q: 'Approximately what percentage of attendees are already customers?', options: ['Less than 10%', '10-30%', '30-60%', '60%+'] },
      { q: 'How well known is your brand in this market?', options: ['Unknown', 'Some awareness', 'Industry leader'] },
      { q: 'Are you participating in additional sponsorship opportunities with the conference?', options: ['Yes', 'No'] },
      { q: 'Do you have any new or newsworthy announcements to make?', options: ['Yes', 'No'], hasFollowUp: true, followUpLabel: 'What is the announcement?', followUpPlaceholder: 'Acquisition / Award / Hiring / Funding / New product...' },
      { q: 'Do you have budget available for sponsorships?', options: ['Yes', 'No'] },
      { q: 'What is your booth size?', options: ['10x10', '10x20', 'Island', 'Large experience'] },
    ],
    'Customer Engagement': [
      { q: 'Approximately what percentage of attendees are existing customers?', options: ['Less than 10%', '10-30%', '30-60%', '60%+'] },
      { q: 'Do you have a list of customers identified that normally attend this show?', options: ['Yes', 'No'] },
      { q: 'Is the attendee list available before the show to cross-reference customers?', options: ['Yes', 'No'] },
      { q: 'Which of the following are you planning to engage customers with?', options: ['Business review', 'Recent research or valuable content', 'Exclusive discount', 'High-end or tiered gifts', 'Scheduled meals or meetings'], multi: true },
      { q: 'Are you open to hosting a connection event (dinner, social, hospitality)?', options: ['Yes', 'No'] },
      { q: 'Who primarily attends this show?', options: ['Executives', 'Engineers', 'Buyers', 'End users', 'Mixed audience'] },
    ],
  };

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    calculateProgress();
  }, [showOverview, generalInfo, travelEntries, teamMembers, marketingEntries, boothItems, strategyAnswers, selectedEvent]);

  // Only auto-select once, so the user can still clear the show afterwards
  // without it snapping straight back.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!eventIdFromUrl || selectedEvent || events.length === 0) return;
    const match = events.find(e => e.id === eventIdFromUrl);
    if (match) {
      autoSelectedRef.current = true;
      handleSelectEvent(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, eventIdFromUrl]);

  async function loadEvents() {
    if (!user.userId) return;
    setLoading(true);
    const result = await fetchEvents(user.userId);
    if (result.success) setEvents(result.data);
    setLoading(false);
  }

  function calculateProgress() {
    let filled = 0;
    let total = 7;
    if (selectedEvent) filled++;
    if (showOverview.showName) filled++;
    if (generalInfo.boothNumber) filled++;
    if (travelEntries[0].hotelName) filled++;
    if (teamMembers[0].memberName) filled++;
    if (marketingEntries[0].mediumName) filled++;
    if (Object.keys(strategyAnswers).length > 0) filled++;
    setProgress(Math.round((filled / total) * 100));
  }

  function toggleSection(section) {
    setSections(prev => ({ ...prev, [section]: !prev[section] }));
  }

  async function handleSelectEvent(event) {
    setSelectedEvent(event);
    setShowOverview(prev => ({
      ...prev,
      showName: event.eventName,
      location: event.venue || '',
      eventDates: event.eventDate ? (event.endDate ? `${event.eventDate} to ${event.endDate}` : event.eventDate) : prev.eventDates,
      targetAudience: event.market || prev.targetAudience,
      notes: event.exhibitorBenefits || prev.notes,
    }));
    setGeneralInfo(prev => ({
      ...prev,
      boothSize: event.boothSize || prev.boothSize,
    }));

    try {
      const res = await fetch(`${API_BASE_URL}/playbooks?userId=${user.userId}&eventId=${event.id}`);
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          const pb = data[0];
          setPlaybookId(pb.id);
          setShowOverview({
            showName: pb.showName || event.eventName,
            exhibitHall: pb.exhibitHall || '',
            eventDates: event.eventDate || '',
            targetAudience: pb.targetAudience || '',
            location: pb.location || event.venue || '',
            locationAddress: pb.locationAddress || '',
            notes: pb.notes || '',
          });
          setGeneralInfo({
            boothNumber: pb.boothNumber || '',
            boothSize: pb.boothSize || '',
            showAttireNotes: pb.showAttireNotes || '',
            badgesAndBoothConduct: pb.badgesAndBoothConduct || '',
            badgePickup: pb.badgePickup || '',
          });
          setTeamNotes(pb.teamNotes || '');
          loadPlaybookItems(pb.id);
        }
      }
    } catch (err) {
      console.error('Failed to load playbook:', err);
    }
  }

  async function loadPlaybookItems(pbId) {
    try {
      const [teamRes, travelRes, marketingRes, boothRes, stratRes] = await Promise.all([
        fetch(`${API_BASE_URL}/playbook-items?playbookId=${pbId}&type=team`),
        fetch(`${API_BASE_URL}/playbook-items?playbookId=${pbId}&type=travel`),
        fetch(`${API_BASE_URL}/playbook-items?playbookId=${pbId}&type=marketing`),
        fetch(`${API_BASE_URL}/playbook-items?playbookId=${pbId}&type=booth`),
        fetch(`${API_BASE_URL}/playbook-items?playbookId=${pbId}&type=strategy`),
      ]);
      if (teamRes.ok) {
        const data = await teamRes.json();
        if (data.length > 0) setTeamMembers(data);
      }
      if (travelRes.ok) {
        const data = await travelRes.json();
        if (data.length > 0) setTravelEntries(data);
      }
      if (marketingRes.ok) {
        const data = await marketingRes.json();
        if (data.length > 0) setMarketingEntries(data);
      }
      if (boothRes.ok) {
        const data = await boothRes.json();
        if (data.length > 0) setBoothItems(data);
      }
      if (stratRes.ok) {
        const data = await stratRes.json();
        if (data.length > 0) {
          const answers = {};
          data.forEach(a => {
            const key = `${a.category}-${a.questionNumber}`;
            const question = strategyQuestions[a.category] && strategyQuestions[a.category][a.questionNumber];
            // Multi-select answers are stored joined, so they are split back
            // into an array or the checkboxes will not reflect what was saved.
            answers[key] = question && question.multi
              ? String(a.answer || '').split(',').map(v => v.trim()).filter(Boolean)
              : a.answer;
            if (a.additionalInfo) {
              answers[`${key}-info`] = a.additionalInfo;
            }
          });
          setStrategyAnswers(answers);
        }
      }
    } catch (err) {
      console.error('Failed to load playbook items:', err);
    }
  }

  async function savePlaybook() {
    const payload = {
      userId: user.userId,
      eventId: selectedEvent.id,
      showName: showOverview.showName,
      exhibitHall: showOverview.exhibitHall,
      targetAudience: showOverview.targetAudience,
      location: showOverview.location,
      locationAddress: showOverview.locationAddress,
      notes: showOverview.notes,
      boothNumber: generalInfo.boothNumber,
      boothSize: generalInfo.boothSize,
      pickupNotes: showOverview.pickupNotes || '',
      showAttireNotes: generalInfo.showAttireNotes,
      badgesAndBoothConduct: generalInfo.badgesAndBoothConduct,
      badgePickup: generalInfo.badgePickup,
      teamNotes: teamNotes,
      progress: progress,
      status: 'draft',
    };

    try {
      let res;
      if (playbookId) {
        res = await fetch(`${API_BASE_URL}/playbooks?id=${playbookId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_BASE_URL}/playbooks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data = await res.json();
          setPlaybookId(data.id);
        }
      }

      if (playbookId || res.ok) {
        const pbId = playbookId || (await res.json()).id;
        await savePlaybookItems(pbId);
      }

      alert('Playbook saved successfully!');
    } catch (err) {
      console.error('Failed to save playbook:', err);
      alert('Failed to save playbook.');
    }
  }
  async function handleBoothPhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setBoothForm({...boothForm, photoPath: data.fileName});
        alert('Photo uploaded!');
      } else {
        alert('Upload failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Upload failed.');
    }
  }


  function formatStrategyAnswers() {
    const lines = [];
    for (const [category, questions] of Object.entries(strategyQuestions)) {
      questions.forEach((q, qIdx) => {
        const ans = strategyAnswers[`${category}-${qIdx}`];
        if (ans && (Array.isArray(ans) ? ans.length : true)) {
          const val = Array.isArray(ans) ? ans.join(', ') : ans;
          const extra = strategyAnswers[`${category}-${qIdx}-info`];
          lines.push(`[${category}] ${q.q} -> ${val}${extra ? ` (${extra})` : ''}`);
        }
      });
    }
    return lines.join('\n');
  }

  function formatRecommendationsText(recs) {
    if (!recs.length) {
      return 'Answer some questions in the Strategy Builder above to see tailored recommendations.';
    }
    return recs.map(r => `• ${r.title}\n   Why: ${r.reason}`).join('\n\n');
  }

  async function generateRecommendations() {
    setGeneratingRecs(true);
    setShowRecommendations(true);

    // --- Tier 1: Deterministic rules layer (always available, no network) ---
    const { recommendations: ruleRecs } = generateStrategyRecommendations(strategyAnswers);
    const rulesText = formatRecommendationsText(ruleRecs);

    // Show the reliable rules output immediately.
    setRecommendations(rulesText);
    setGeneratingRecs(false);

    // --- Tier 2: AI-polish overlay (additive; never blocks or breaks Tier 1) ---
    // The rules decide WHAT to recommend; the AI only improves HOW it reads.
    // On ANY failure (timeout, error, service down) we keep the rules text
    // already shown, so worst case equals today's behavior.
    if (!ruleRecs.length) return;
    setPolishing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'strategy',
          showName: showOverview.showName,
          market: showOverview.targetAudience,
          targetAudience: showOverview.targetAudience,
          location: showOverview.location,
          mode: 'naive',
          answers:
            'Rephrase the following trade-show recommendations into polished, ' +
            'show-specific prose. Do NOT add, remove, or change any recommendation ' +
            '\u2014 only improve the wording and tailor it to this show:\n\n' + rulesText,
        }),
      });
      const data = await res.json();
      if (data && data.success) {
        const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
        const polished = content && content.response ? String(content.response).trim() : '';
        if (polished) {
          setRecommendations(polished);
        }
      }
    } catch (err) {
      console.log('AI polish unavailable, using rules output:', err && err.message);
    }
    setPolishing(false);
  }
  
  async function savePlaybookItems(pbId) {
    for (const member of teamMembers) {
      if (member.memberName && !member.id) {
        await fetch(`${API_BASE_URL}/playbook-items?type=team`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ playbookId: pbId, ...member }),
        });
      }
    }
    for (const travel of travelEntries) {
      if (travel.representativeName && !travel.id) {
        await fetch(`${API_BASE_URL}/playbook-items?type=travel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ playbookId: pbId, ...travel }),
        });
      }
    }
    for (const marketing of marketingEntries) {
      if (marketing.mediumName && !marketing.id) {
        await fetch(`${API_BASE_URL}/playbook-items?type=marketing`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ playbookId: pbId, ...marketing }),
        });
      }
    }
    for (const item of boothItems) {
      if (item.itemType && !item.id) {
        await fetch(`${API_BASE_URL}/playbook-items?type=booth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ playbookId: pbId, ...item }),
        });
      }
    }
    // Replace rather than append: without this every save inserts another
    // copy of every answer, so the same question accumulates duplicates.
    if (Object.keys(strategyAnswers).length > 0) {
      await fetch(`${API_BASE_URL}/playbook-items?type=strategy&playbookId=${pbId}`, {
        method: 'DELETE',
      });
    }

    for (const [key, answer] of Object.entries(strategyAnswers)) {
      const [category, qNum, suffix] = key.split('-');
      // Follow-up text is stored against its parent question, not as a row.
      if (suffix === 'info') continue;

      const qIdx = parseInt(qNum);
      const question = strategyQuestions[category] && strategyQuestions[category][qIdx];
      // Without the question text a shared or exported answer is meaningless,
      // so it is saved alongside the answer rather than left blank.
      const questionText = question ? question.q : '';
      const additionalInfo = strategyAnswers[`${category}-${qNum}-info`] || '';
      const answerText = Array.isArray(answer) ? answer.join(', ') : answer;
      if (!answerText) continue;

      await fetch(`${API_BASE_URL}/playbook-items?type=strategy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playbookId: pbId, category, questionNumber: qIdx,
          questionText, answer: answerText, additionalInfo,
        }),
      });
    }
  }

  function addTeamMember() {
    setTeamMembers([...teamMembers, { memberName: '', contactTitle: '', startDate: '', startTime: '', endDate: '', endTime: '' }]);
  }

  function removeTeamMember(index) {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  }

  function updateTeamMember(index, field, value) {
    const updated = [...teamMembers];
    updated[index] = { ...updated[index], [field]: value };
    setTeamMembers(updated);
  }

  function addTravelEntry() {
    setTravelEntries([...travelEntries, {
      representativeName: '', arrivalDate: '', arrivalTime: '', departureDate: '', departureTime: '',
      hotelReservationNumber: '', hotelTotalCost: '', hotelName: '', hotelAddress: '',
      airfareCost: '', carMileageCost: '', trainCost: '', rentalCarCost: '',
    }]);
  }

  function removeTravelEntry(index) {
    setTravelEntries(travelEntries.filter((_, i) => i !== index));
  }

  function updateTravelEntry(index, field, value) {
    const updated = [...travelEntries];
    updated[index] = { ...updated[index], [field]: value };
    setTravelEntries(updated);
  }

  function addMarketingEntry() {
    setMarketingEntries([...marketingEntries, { mediumName: '', scheduleDate: '', keyMessages: '', callToAction: '', representativeName: '' }]);
  }

  function removeMarketingEntry(index) {
    setMarketingEntries(marketingEntries.filter((_, i) => i !== index));
  }

  function updateMarketingEntry(index, field, value) {
    const updated = [...marketingEntries];
    updated[index] = { ...updated[index], [field]: value };
    setMarketingEntries(updated);
  }

  function addBoothItem() {
    setBoothItems([...boothItems, { ...boothForm }]);
    setBoothForm({
      itemType: '', storageLocation: '', storageArea: '', quantity: '',
      eventLocation: '', startDate: '', endDate: '', status: '',
      description: '', itemWeight: '0', itemLength: '0', itemWidth: '0', itemHeight: '0',
      pkgWeight: '0', pkgLength: '0', pkgWidth: '0', pkgHeight: '0',
    });
  }

  function removeBoothItem(index) {
    setBoothItems(boothItems.filter((_, i) => i !== index));
  }

  const filteredEvents = events.filter(e =>
    e.eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.venue && e.venue.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // RENDER
  if (showRecommendations) {
    return (
      <DashboardLayout>
        <h1 className="page-title">Trade Show Strategy Recommendations</h1>
        <div className="pb-recommendations">
          <div className="pb-recommendations-header">
            <span className="pb-section-title open">Trade Show Strategy Recommendations</span>
          </div>
          <div className="pb-recommendations-intro">
            Here are Zibit's suggestions to achieving your goals at the show. Please check which you'd like to use. Feel free to edit as you see fit!
          </div>
          {generatingRecs ? (
            <div className="pb-generating">
              <div className="pb-spinner"></div>
              <p>Generating AI-powered recommendations...</p>
              <p className="pb-generating-sub">This may take up to a minute.</p>
            </div>
          ) : (
            <>
              {editingRecs ? (
                <textarea
                  className="pb-recommendations-editor"
                  value={recommendations}
                  onChange={e => setRecommendations(e.target.value)}
                  rows="20"
                />
              ) : (
                <div className="pb-recommendations-content">
                  {recommendations.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              )}
              <div className="pb-recommendations-actions">
                {editingRecs ? (
                  <button className="pb-btn-save" onClick={() => setEditingRecs(false)}>💾 Save</button>
                ) : (
                  <button className="pb-btn-edit" onClick={() => setEditingRecs(true)}>✏️ Edit</button>
                )}
                <button className="pb-btn-proceed" onClick={() => { setShowRecommendations(false); setShowPreview(true); }}>
                  Proceed &gt;
                </button>
              </div>
            </>
          )}
          <button className="pb-back-link" onClick={() => setShowRecommendations(false)}>&lt; Back to Strategy Builder</button>
        </div>
      </DashboardLayout>
    );
  }

  if (showPreview) {
    return (
      <DashboardLayout>
        <div className="pb-preview">
          <h1 className="pb-preview-title">PlayBook Preview</h1>
          <div className="pb-preview-card">
            <h2>SHOW OVERVIEW & STRATEGY</h2>
            <div className="pb-preview-field"><strong>Show Name:</strong> {showOverview.showName}</div>
            <div className="pb-preview-field"><strong>Date:</strong> {showOverview.eventDates}</div>
            <div className="pb-preview-field"><strong>Location:</strong> {showOverview.location}</div>
            <div className="pb-preview-field"><strong>Exhibit Hall:</strong> {showOverview.exhibitHall}</div>
            <div className="pb-preview-field"><strong>Target Audience:</strong> {showOverview.targetAudience}</div>
            <div className="pb-preview-field"><strong>Booth:</strong> #{generalInfo.boothNumber} ({generalInfo.boothSize})</div>
            {teamMembers.filter(m => m.memberName).length > 0 && (
              <>
                <h3>Team</h3>
                {teamMembers.filter(m => m.memberName).map((m, i) => (
                  <div key={i} className="pb-preview-field">{m.memberName} — {m.contactTitle}</div>
                ))}
              </>
            )}
            {boothItems.length > 0 && (
              <>
                <h3>Booth Materials</h3>
                {boothItems.map((b, i) => (
                  <div key={i} className="pb-preview-field">{b.itemType} — {b.storageLocation}</div>
                ))}
              </>
            )}
            {Object.keys(strategyAnswers).length > 0 && (
              <>
                <h3>Strategy Answers</h3>
                {Object.entries(strategyAnswers).map(([key, val]) => (
                  <div key={key} className="pb-preview-field"><strong>{key}:</strong> {Array.isArray(val) ? val.join(', ') : val}</div>
                ))}
              </>
            )}
          </div>
          <div className="pb-preview-actions">
            <button className="pb-btn-edit" onClick={() => { setShowPreview(false); }}>
              <FiEdit2 size={16} /> Edit
            </button>
            <button className="pb-btn-download" onClick={() => window.print()}>
              📥 Download PDF
            </button>
            <button className="pb-btn-share" onClick={() => {
              const url = window.location.href;
              navigator.clipboard.writeText(url);
              alert('Link copied to clipboard!');
            }}>
              🔗 Share Link
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <h1 className="page-title">PlayBook</h1>

      <div className="pb-steps">
        <button className={`pb-step ${step === 1 ? 'active' : ''}`} onClick={() => setStep(1)}>
          <span className="pb-step-num">1</span> Select Show
        </button>
        <button className={`pb-step ${step === 2 ? 'active' : ''}`} onClick={() => selectedEvent && setStep(2)}>
          <span className="pb-step-num">2</span> Strategy Builder
        </button>
      </div>

      {step === 1 && !selectedEvent && (
        <div className="pb-select-show">
          <h3>Search and Select Show / Event</h3>
          <div className="pb-search-box">
            <input
              type="text"
              placeholder="Search Show / Events / Type"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FiSearch className="pb-search-icon" size={16} />
          </div>
          <div className="pb-event-list">
            {loading ? <p className="loading-text">Loading...</p> :
              filteredEvents.map(event => (
                <div className="pb-event-card" key={event.id} onClick={() => handleSelectEvent(event)}>
                  <div className="pb-event-info">
                    <strong>{event.eventName}</strong>
                    <span>{event.venue}</span>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {step === 1 && selectedEvent && (
        <div className="pb-selected-show">
          <div className="pb-selected-header">
            <div>
              <span>You have selected</span>
              <div className="pb-selected-card">
                <strong>{selectedEvent.eventName}</strong>
                <span>{selectedEvent.venue}</span>
                <button className="pb-share-btn" onClick={() => setShowShareDialog(true)} title="Share this show">
                  Share
                </button>
                <button className="pb-remove-btn" onClick={() => { setSelectedEvent(null); setPlaybookId(null); }}>
                  <FiTrash2 size={16} />
                </button>
              </div>
            </div>
          </div>

          <div className="pb-sections">
            <div className="pb-section">
              <div className="pb-section-header" onClick={() => toggleSection('showOverview')}>
                <span className={`pb-section-title ${sections.showOverview ? 'open' : ''}`}>Show Overview / Information</span>
                {sections.showOverview ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections.showOverview && (
                <div className="pb-section-body">
                  {/* These details belong to the event, so they are shown here
                      rather than duplicated. Editing happens in one place. */}
                  <div className="pb-overview-grid">
                    <ShowField label="Show Name" value={selectedEvent.eventName} />
                    <ShowField label="Location" value={selectedEvent.venue} />
                    <ShowField label="Website" value={selectedEvent.website} />
                    <ShowField label="Start Date" value={selectedEvent.eventDate} />
                    <ShowField label="End Date" value={selectedEvent.endDate} />
                    <ShowField label="Exhibit Start" value={selectedEvent.exhibitStartDate} />
                    <ShowField label="Exhibit End" value={selectedEvent.exhibitEndDate} />
                    <ShowField label="Target Audience" value={selectedEvent.market} />
                    <ShowField label="Contact Titles" value={selectedEvent.contactTitles} />
                    <ShowField label="Number of Attendees" value={selectedEvent.numberOfAttendees} />
                    <ShowField label="Call for Presentation" value={selectedEvent.callForPresentationDate} />
                    <ShowField label="Booth Size" value={selectedEvent.boothSize} />
                    <ShowField label="Budget (expected)" value={selectedEvent.budget} />
                  </div>

                  <ShowField label="Sponsorship Opportunities" value={selectedEvent.sponsorshipOpportunities} block />
                  <ShowField label="Other Speaking Opportunities" value={selectedEvent.otherSpeakingOpportunities} block />
                  <ShowField label="Exhibitor Contact Info" value={selectedEvent.exhibitorContactInfo} block />
                  <ShowField label="Exhibitor Benefits" value={selectedEvent.exhibitorBenefits} block />

                  <div className="pb-section-actions">
                    <button
                      className="pb-btn-confirm"
                      onClick={() => navigate(`/events?edit=${selectedEvent.id}`)}
                    >
                      Edit event details
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="pb-section">
              <div className="pb-section-header" onClick={() => toggleSection('generalInfo')}>
                <span className={`pb-section-title ${sections.generalInfo ? 'open' : ''}`}>General Information</span>
                {sections.generalInfo ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections.generalInfo && (
                <div className="pb-section-body">
                  <div className="pb-form-row">
                    <div className="pb-form-group">
                      <label>Booth #</label>
                      <input type="text" value={generalInfo.boothNumber} onChange={e => setGeneralInfo({...generalInfo, boothNumber: e.target.value})} placeholder="#" />
                    </div>
                    <div className="pb-form-group">
                      <label>Size</label>
                      <input type="text" value={generalInfo.boothSize} onChange={e => setGeneralInfo({...generalInfo, boothSize: e.target.value})} placeholder="10X20" />
                    </div>
                    
                  </div>
                  <div className="pb-form-group full">
                    <label>Show Attire Notes</label>
                    <textarea value={generalInfo.showAttireNotes} onChange={e => setGeneralInfo({...generalInfo, showAttireNotes: e.target.value})} placeholder="What should the team wear? e.g. branded polo and dark trousers, closed-toe shoes for set-up" rows="3" />
                  </div>
                  <div className="pb-form-group full">
                    <label>Booth Conduct</label>
                    <textarea value={generalInfo.badgesAndBoothConduct} onChange={e => setGeneralInfo({...generalInfo, badgesAndBoothConduct: e.target.value})} placeholder="Expectations at the booth: no phones or sitting, greet visitors standing, no eating in the booth, stay until close" rows="3" />
                  </div>
                  <div className="pb-form-group full">
                    <label>Badge Pickup</label>
                    <textarea value={generalInfo.badgePickup} onChange={e => setGeneralInfo({...generalInfo, badgePickup: e.target.value})} placeholder="Where and when to collect badges" rows="3" />
                  </div>
                  <div className="pb-section-actions">
                    <button className="pb-btn-clear">Clear</button>
                    <button className="pb-btn-confirm" onClick={savePlaybook}>Confirm</button>
                  </div>
                </div>
              )}
            </div>

            <div className="pb-section">
              <div className="pb-section-header" onClick={() => toggleSection('travelInfo')}>
                <span className={`pb-section-title ${sections.travelInfo ? 'open' : ''}`}>Travel Information</span>
                {sections.travelInfo ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections.travelInfo && (
                <div className="pb-section-body">
                  {travelEntries.map((entry, idx) => (
                    <div key={idx} className="pb-travel-entry">
                      <div className="pb-form-row">
                        <div className="pb-form-group">
                          <label>Company Representative</label>
                          <input type="text" value={entry.representativeName} onChange={e => updateTravelEntry(idx, 'representativeName', e.target.value)} placeholder="Name" />
                        </div>
                      </div>
                      <div className="pb-form-row">
                        <div className="pb-form-group">
                          <label>Arrive by</label>
                          <div className="pb-datetime">
                            <input type="date" value={entry.arrivalDate} onChange={e => updateTravelEntry(idx, 'arrivalDate', e.target.value)} />
                            <input type="time" value={entry.arrivalTime || ''} onChange={e => updateTravelEntry(idx, 'arrivalTime', e.target.value)} />
                          </div>
                        </div>
                        <div className="pb-form-group">
                          <label>Depart After</label>
                          <div className="pb-datetime">
                            <input type="date" value={entry.departureDate} onChange={e => updateTravelEntry(idx, 'departureDate', e.target.value)} />
                            <input type="time" value={entry.departureTime || ''} onChange={e => updateTravelEntry(idx, 'departureTime', e.target.value)} />
                          </div>
                        </div>
                      </div>
                      <div className="pb-form-row">
                        <div className="pb-form-group">
                          <label>Hotel Reservation Number</label>
                          <input type="text" value={entry.hotelReservationNumber} onChange={e => updateTravelEntry(idx, 'hotelReservationNumber', e.target.value)} />
                        </div>
                        <div className="pb-form-group">
                          <label>Hotel Total Cost</label>
                          <input type="text" value={entry.hotelTotalCost} onChange={e => updateTravelEntry(idx, 'hotelTotalCost', e.target.value)} />
                        </div>
                      </div>
                      <div className="pb-form-group full">
                        <label>Hotel Name</label>
                        <input type="text" value={entry.hotelName} onChange={e => updateTravelEntry(idx, 'hotelName', e.target.value)} placeholder="Enter Hotel Name" />
                      </div>
                      <div className="pb-form-group full">
                        <label>Hotel Address</label>
                        <textarea value={entry.hotelAddress} onChange={e => updateTravelEntry(idx, 'hotelAddress', e.target.value)} placeholder="Enter Hotel Address" rows="2" />
                      </div>
                      <p className="pb-subtitle">Travel Cost</p>
                      <div className="pb-cost-rows">
                        <div className="pb-cost-row"><span>Airfare Cost</span><input type="text" value={entry.airfareCost} onChange={e => updateTravelEntry(idx, 'airfareCost', e.target.value)} placeholder="Value" /></div>
                        <div className="pb-cost-row"><span>Car Mileage Cost</span><input type="text" value={entry.carMileageCost} onChange={e => updateTravelEntry(idx, 'carMileageCost', e.target.value)} placeholder="Value" /></div>
                        <div className="pb-cost-row"><span>Train Cost</span><input type="text" value={entry.trainCost} onChange={e => updateTravelEntry(idx, 'trainCost', e.target.value)} placeholder="Value" /></div>
                        <div className="pb-cost-row"><span>Rental Car Cost</span><input type="text" value={entry.rentalCarCost} onChange={e => updateTravelEntry(idx, 'rentalCarCost', e.target.value)} placeholder="Value" /></div>
                      </div>
                      {travelEntries.length > 1 && (
                        <button className="pb-remove-entry" onClick={() => removeTravelEntry(idx)}>Remove Representative</button>
                      )}
                      {idx < travelEntries.length - 1 && <hr className="pb-divider" />}
                    </div>
                  ))}
                  <button className="pb-add-btn" onClick={addTravelEntry}><FiPlus size={14} /> Add Representative</button>
                  <div className="pb-section-actions">
                    <button className="pb-btn-clear">Clear</button>
                    <button className="pb-btn-confirm" onClick={savePlaybook}>Save</button>
                  </div>
                </div>
              )}
            </div>

            <div className="pb-section">
              <div className="pb-section-header" onClick={() => toggleSection('teamSchedule')}>
                <span className={`pb-section-title ${sections.teamSchedule ? 'open' : ''}`}>Team Schedule</span>
                {sections.teamSchedule ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections.teamSchedule && (
                <div className="pb-section-body">
                  <div className="pb-form-row">
                    <div className="pb-form-group">
                      <label>Arrival date</label>
                      <input type="date" value={teamDates.arrivalDate} onChange={e => setTeamDates({...teamDates, arrivalDate: e.target.value})} />
                    </div>
                    <div className="pb-form-group">
                      <label>Departure date</label>
                      <input type="date" value={teamDates.departureDate} onChange={e => setTeamDates({...teamDates, departureDate: e.target.value})} />
                    </div>
                  </div>
                  <p className="pb-subtitle">Team Members</p>
                  {teamMembers.map((member, idx) => (
                    <div key={idx} className="pb-team-row">
                      <input type="text" value={member.memberName} onChange={e => updateTeamMember(idx, 'memberName', e.target.value)} placeholder="Name" />
                      <select value={member.contactTitle} onChange={e => updateTeamMember(idx, 'contactTitle', e.target.value)}>
                        <option value="">Team Member Role</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Sales">Sales</option>
                        <option value="Subject Matter Expert">Subject Matter Expert</option>
                        <option value="Speaker">Speaker</option>
                        <option value="Operations">Operations</option>
                        <option value="Executive">Executive</option>
                        <option value="VIP">VIP</option>
                      </select>
                      <div className="pb-datetime">
                        <input type="date" value={member.startDate} onChange={e => updateTeamMember(idx, 'startDate', e.target.value)} />
                        <input type="time" value={member.startTime || ''} onChange={e => updateTeamMember(idx, 'startTime', e.target.value)} />
                      </div>
                      <div className="pb-datetime">
                        <input type="date" value={member.endDate} onChange={e => updateTeamMember(idx, 'endDate', e.target.value)} />
                        <input type="time" value={member.endTime || ''} onChange={e => updateTeamMember(idx, 'endTime', e.target.value)} />
                      </div>
                      <div className="pb-team-actions">
                        <button className="pb-icon-btn" onClick={addTeamMember}><FiPlus size={14} /></button>
                        <button className="pb-icon-btn delete" onClick={() => removeTeamMember(idx)}><FiTrash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                  <div className="pb-form-group full">
                    <label>Team Notes</label>
                    <textarea value={teamNotes} onChange={e => setTeamNotes(e.target.value)} placeholder="Value" rows="3" />
                  </div>
                  <div className="pb-section-actions">
                    <button className="pb-btn-clear">Clear</button>
                    <button className="pb-btn-confirm" onClick={savePlaybook}>Confirm</button>
                  </div>
                </div>
              )}
            </div>

            <div className="pb-section">
              <div className="pb-section-header" onClick={() => toggleSection('marketingSchedule')}>
                <span className={`pb-section-title ${sections.marketingSchedule ? 'open' : ''}`}>Marketing Schedule</span>
                {sections.marketingSchedule ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections.marketingSchedule && (
                <div className="pb-section-body">
                  <div className="pb-form-group">
                    <label>Select Company Representative</label>
                    <select value={marketingRep} onChange={e => setMarketingRep(e.target.value)}>
                      <option value="">Select Company Representative</option>
                      {teamMembers.filter(m => m.memberName).map((m, i) => (
                        <option key={i} value={m.memberName}>{m.memberName}</option>
                      ))}
                    </select>
                  </div>
                  {marketingEntries.map((entry, idx) => (
                    <div key={idx} className="pb-marketing-row">
                      <input type="text" value={entry.mediumName} onChange={e => updateMarketingEntry(idx, 'mediumName', e.target.value)} placeholder="Enter Medium Name" />
                      <input type="date" value={entry.scheduleDate} onChange={e => updateMarketingEntry(idx, 'scheduleDate', e.target.value)} />
                      <textarea value={entry.keyMessages} onChange={e => updateMarketingEntry(idx, 'keyMessages', e.target.value)} placeholder="Enter Key Messages" rows="2" />
                      <textarea value={entry.callToAction} onChange={e => updateMarketingEntry(idx, 'callToAction', e.target.value)} placeholder="Call to Action" rows="2" />
                      <div className="pb-team-actions">
                        <button className="pb-icon-btn" onClick={addMarketingEntry}><FiPlus size={14} /></button>
                        <button className="pb-icon-btn delete" onClick={() => removeMarketingEntry(idx)}><FiTrash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                  <div className="pb-section-actions">
                    <button className="pb-btn-clear">Clear</button>
                    <button className="pb-btn-confirm" onClick={savePlaybook}>Confirm</button>
                  </div>
                </div>
              )}
            </div>

            <div className="pb-section">
              <div className="pb-section-header" onClick={() => toggleSection('boothMaterials')}>
                <span className={`pb-section-title ${sections.boothMaterials ? 'open' : ''}`}>Booth Materials Checklist</span>
                {sections.boothMaterials ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections.boothMaterials && (
                <div className="pb-section-body">
                  <div className="pb-form-row">
                    <div className="pb-form-group">
                      <label>Type / Make / Model</label>
                      <input type="text" value={boothForm.itemType} onChange={e => setBoothForm({...boothForm, itemType: e.target.value})} placeholder="Enter Type / Make / Model Name" />
                    </div>
                    <div className="pb-form-group">
                      <label>Availability Dates</label>
                      <div className="pb-date-range">
                        <input type="date" value={boothForm.startDate} onChange={e => setBoothForm({...boothForm, startDate: e.target.value})} />
                        <input type="date" value={boothForm.endDate} onChange={e => setBoothForm({...boothForm, endDate: e.target.value})} />
                      </div>
                    </div>
                  </div>
                  <div className="pb-form-row">
                    <div className="pb-form-group">
                      <label>Storage Location</label>
                      <input type="text" value={boothForm.storageLocation} onChange={e => setBoothForm({...boothForm, storageLocation: e.target.value})} placeholder="Enter Storage Location" />
                    </div>
                    <div className="pb-form-group">
                      <label>Storage Area</label>
                      <input type="text" value={boothForm.storageArea} onChange={e => setBoothForm({...boothForm, storageArea: e.target.value})} placeholder="Enter Storage Area" />
                    </div>
                  </div>
                  <div className="pb-form-row">
                    <div className="pb-form-group">
                      <label>Quantity</label>
                      <input type="text" value={boothForm.quantity} onChange={e => setBoothForm({...boothForm, quantity: e.target.value})} placeholder="Enter Quantity" />
                    </div>
                    <div className="pb-form-group">
                      <label>Event Location</label>
                      <input type="text" value={boothForm.eventLocation} onChange={e => setBoothForm({...boothForm, eventLocation: e.target.value})} placeholder="Enter Event Location" />
                    </div>
                  </div>
                  <div className="pb-form-group full">
                    <label>Photo Upload</label>
                    <input type="file" accept="image/*" onChange={handleBoothPhotoUpload} />
                    {boothForm.photoPath && (
                      <p style={{fontSize: '13px', color: '#22c55e', marginTop: '4px'}}>✓ {boothForm.photoPath}</p>
                    )}
                  </div>
                  <div className="pb-form-group full">
                    <label>Description</label>
                    <textarea value={boothForm.description} onChange={e => setBoothForm({...boothForm, description: e.target.value})} placeholder="Enter Description" rows="3" />
                  </div>
                  <p className="pb-subtitle">Weight & Dimensions</p>
                  <div className="pb-dimensions-grid">
                    <div><label>Item Weight (lbs)</label><input type="text" value={boothForm.itemWeight} onChange={e => setBoothForm({...boothForm, itemWeight: e.target.value})} /></div>
                    <div><label>Item Length (in)</label><input type="text" value={boothForm.itemLength} onChange={e => setBoothForm({...boothForm, itemLength: e.target.value})} /></div>
                    <div><label>Item Width (in)</label><input type="text" value={boothForm.itemWidth} onChange={e => setBoothForm({...boothForm, itemWidth: e.target.value})} /></div>
                    <div><label>Item Height (in)</label><input type="text" value={boothForm.itemHeight} onChange={e => setBoothForm({...boothForm, itemHeight: e.target.value})} /></div>
                  </div>
                  <p className="pb-subtitle">Packaged Weight & Dimensions</p>
                  <div className="pb-dimensions-grid">
                    <div><label>Pkg Weight (lbs)</label><input type="text" value={boothForm.pkgWeight} onChange={e => setBoothForm({...boothForm, pkgWeight: e.target.value})} /></div>
                    <div><label>Pkg Length (in)</label><input type="text" value={boothForm.pkgLength} onChange={e => setBoothForm({...boothForm, pkgLength: e.target.value})} /></div>
                    <div><label>Pkg Width (in)</label><input type="text" value={boothForm.pkgWidth} onChange={e => setBoothForm({...boothForm, pkgWidth: e.target.value})} /></div>
                    <div><label>Pkg Height (in)</label><input type="text" value={boothForm.pkgHeight} onChange={e => setBoothForm({...boothForm, pkgHeight: e.target.value})} /></div>
                  </div>
                  <button className="pb-add-btn" onClick={addBoothItem}><FiPlus size={14} /> Add Item</button>

                  {boothItems.length > 0 && (
                    <table className="pb-booth-table">
                      <thead>
                        <tr>
                          <th>Item Type / Model</th>
                          <th>Storage Location</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boothItems.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.itemType}</td>
                            <td>{item.storageLocation}</td>
                            <td>{item.startDate}</td>
                            <td>{item.endDate}</td>
                            <td><span className={`pb-status ${item.status === 'active' ? 'active' : 'inactive'}`}>{item.status || 'Active'}</span></td>
                            <td><button className="pb-icon-btn delete" onClick={() => removeBoothItem(idx)}><FiTrash2 size={14} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="pb-section-actions">
                    <button className="pb-btn-clear">Clear</button>
                    <button className="pb-btn-confirm" onClick={savePlaybook}>Confirm</button>
                  </div>
                </div>
              )}
            </div>

            <div className="pb-section">
              <div className="pb-section-header" onClick={() => toggleSection('projectOverview')}>
                <span className={`pb-section-title ${sections.projectOverview ? 'open' : ''}`}>Project Overview</span>
                {sections.projectOverview ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections.projectOverview && (
                <div className="pb-section-body">
                  <p className="pb-overview-text">Complete all sections above to generate your project overview.</p>
                </div>
              )}
            </div>
          </div>

          <div className="pb-progress-bar">
            <div className="pb-progress-fill" style={{ width: `${progress}%` }}>
              <span>{progress}%</span>
            </div>
          </div>

          <div className="pb-bottom-actions">
            <button className="pb-btn-next" onClick={() => setStep(2)}>Next &gt;&gt;</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="pb-strategy">
          {Object.entries(strategyQuestions).map(([category, questions]) => (
            <div className="pb-section" key={category}>
              <div className="pb-section-header" onClick={() => toggleSection(category)}>
                <span className={`pb-section-title ${sections[category] ? 'open' : ''}`}>{category}</span>
                {sections[category] ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
              </div>
              {sections[category] && (
                <div className="pb-section-body">
                  {questions.map((q, qIdx) => (
                    <div key={qIdx} className="pb-question">
                      <p className="pb-question-text">{qIdx + 1}. {q.q}</p>
                      <div className="pb-options">
                        {q.multi ? (
                          q.options.map((opt, oIdx) => (
                            <label key={oIdx} className="pb-checkbox-label">
                              <input
                                type="checkbox"
                                checked={(strategyAnswers[`${category}-${qIdx}`] || []).includes(opt)}
                                onChange={() => {
                                  const key = `${category}-${qIdx}`;
                                  const current = strategyAnswers[key] || [];
                                  const updated = current.includes(opt) ? current.filter(v => v !== opt) : [...current, opt];
                                  setStrategyAnswers({...strategyAnswers, [key]: updated});
                                }}
                              />
                              {opt}
                            </label>
                          ))
                        ) : (
                          q.options.map((opt, oIdx) => (
                            <label key={oIdx} className="pb-radio-label">
                              <input
                                type="radio"
                                name={`${category}-${qIdx}`}
                                checked={strategyAnswers[`${category}-${qIdx}`] === opt}
                                onChange={() => setStrategyAnswers({...strategyAnswers, [`${category}-${qIdx}`]: opt})}
                              />
                              {opt}
                            </label>
                          ))
                        )}
                      </div>
                      {q.hasFollowUp && strategyAnswers[`${category}-${qIdx}`] === 'Yes' && (
                        <div className="pb-follow-up">
                          <label>{q.followUpLabel}</label>
                          <input
                            type="text"
                            placeholder={q.followUpPlaceholder}
                            value={strategyAnswers[`${category}-${qIdx}-info`] || ''}
                            onChange={e => setStrategyAnswers({...strategyAnswers, [`${category}-${qIdx}-info`]: e.target.value})}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="pb-section-actions">
                    <button className="pb-btn-clear">Clear</button>
                    <button className="pb-btn-confirm" onClick={savePlaybook}>Save</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="pb-progress-bar">
            <div className="pb-progress-fill" style={{ width: `${progress}%` }}>
              <span>{progress}%</span>
            </div>
          </div>

          <div className="pb-bottom-actions">
            <button className="pb-btn-preview" onClick={() => { savePlaybook(); generateRecommendations(); }}>Preview</button>
          </div>
        </div>
      )}
      {showShareDialog && selectedEvent && (
        <ShareDialog
          event={selectedEvent}
          user={user}
          onClose={() => setShowShareDialog(false)}
        />
      )}

    </DashboardLayout>
  );
}

/**
 * One read-only detail from the event. Blank values are skipped so a
 * partly-filled show does not render a column of empty rows.
 */
function ShowField({ label, value, block }) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  return (
    <div className={`pb-overview-field ${block ? 'block' : ''}`}>
      <span className="pb-overview-label">{label}</span>
      <span className="pb-overview-value">{String(value)}</span>
    </div>
  );
}

export default PlayBook;
